/**
 * scheduleRunHelpers.js
 *
 * Shared utilities for running a scheduled report locally (i.e. opening the
 * matching report page pre-filled with the schedule's filters + time range
 * and auto-triggering the report). Used by ScheduledReportsPage's
 * "Run Report Local" kebab action.
 *
 * Three concerns live here:
 *   - parseFilterTerms(row.report_filter_terms) — convert the DB-shaped
 *     filter JSON into the flat ID-array shape the report pages consume.
 *   - intervalIdToRange(report_interval_id) — turn 1-8 into a {start,end}
 *     pair using the same definitions as TIME_RANGES in the report pages.
 *   - resolveScheduleRoute(row) — map report_type_id to { path, subType }
 *     so the caller knows where to navigate and which dropdown value to
 *     pre-select on the destination page.
 */

import { getToday, getWeekRange, getMonthRange, getQuarterRange } from '../../../../libs.jsx';

// ---------------------------------------------------------------------------
// Filter shape: DB JSON  →  report page state
// ---------------------------------------------------------------------------
// DB shape: { area_filter: { values: "1,2,3", ... }, ... }
// Page shape: { areas: [1, 2, 3], ... }
//
// Report pages keep these as arrays (or Sets in some) — we hand back plain
// arrays since `state.filters` consumers spread or coerce as needed.
const FILTER_KEY_MAP = {
    area_filter: 'areas',
    area_group_filter: 'areaGroups',
    area_type_filter: 'areaTypes',
    resident_filter: 'residents',
    resident_group_filter: 'residentGroups',
    user_filter: 'users',
    user_group_filter: 'userGroups',
    device_category_filter: 'deviceCategories',
    device_filter: 'devices',
    event_type_filter: 'eventTypes',
    reason_filter: 'reasons',
};

export function parseFilterTerms(filterTerms) {
    if (!filterTerms) return {};
    let parsed;
    try {
        parsed = typeof filterTerms === 'string' ? JSON.parse(filterTerms) : filterTerms;
    } catch {
        return {};
    }

    const out = {};
    Object.entries(FILTER_KEY_MAP).forEach(([dbKey, stateKey]) => {
        const val = parsed?.[dbKey]?.values;
        if (val == null || val === '') {
            out[stateKey] = [];
        } else {
            out[stateKey] = String(val).split(',').filter(Boolean);
        }
    });

    // Response time gets a richer shape so consumers know whether it was
    // active and how to render it.
    const rv = parsed?.resp_filter_items?.values;
    if (Array.isArray(rv) && rv.length >= 3) {
        out.responseTime = {
            enabled: Boolean(rv[0]),
            operator: rv[1] || 'lt',
            value: parseInt(rv[2], 10) || 60,
        };
    }
    return out;
}

// ---------------------------------------------------------------------------
// Interval ID → { startDate, endDate }
// ---------------------------------------------------------------------------
// IDs 1–8 mirror TIME_RANGES across the report pages. Returns Date objects
// (start at 00:00:00, end at 23:59:59 unless the range ends at "now").
export function intervalIdToRange(reportIntervalId) {
    const id = parseInt(reportIntervalId, 10);
    const now = new Date();
    let start;
    let end;

    switch (id) {
        case 1: { // Today
            start = getToday(); start.setHours(0, 0, 0, 0);
            end = new Date();
            break;
        }
        case 2: { // Yesterday
            start = getToday(-1); start.setHours(0, 0, 0, 0);
            end = getToday(-1); end.setHours(23, 59, 59, 999);
            break;
        }
        case 3: { // This Week
            const [s] = getWeekRange(0); s.setHours(0, 0, 0, 0);
            start = s; end = now;
            break;
        }
        case 4: { // This Month
            const [s] = getMonthRange(0); s.setHours(0, 0, 0, 0);
            start = s; end = now;
            break;
        }
        case 5: { // This Quarter
            const [s] = getQuarterRange(0); s.setHours(0, 0, 0, 0);
            start = s; end = now;
            break;
        }
        case 6: { // Last Week
            const [s, e] = getWeekRange(-1);
            s.setHours(0, 0, 0, 0); e.setHours(23, 59, 59, 999);
            start = s; end = e;
            break;
        }
        case 7: { // Last Month
            const [s, e] = getMonthRange(-1);
            s.setHours(0, 0, 0, 0); e.setHours(23, 59, 59, 999);
            start = s; end = e;
            break;
        }
        case 8: { // Last Quarter
            const [s, e] = getQuarterRange(-1);
            s.setHours(0, 0, 0, 0); e.setHours(23, 59, 59, 999);
            start = s; end = e;
            break;
        }
        default: { // Fallback — Yesterday
            start = getToday(-1); start.setHours(0, 0, 0, 0);
            end = getToday(-1); end.setHours(23, 59, 59, 999);
        }
    }
    return { startDate: start, endDate: end, rangeId: id };
}

// ---------------------------------------------------------------------------
// Report-type ID → destination
// ---------------------------------------------------------------------------
// Returns { path, subType } so the caller knows where to navigate and which
// sub-type to pre-select on the destination page.
//
//   - Alert Details (id 1)             → /reports/alerts/run  (no subType)
//   - Hourly Summary (id 4)            → /reports/hourly
//   - Executive (id 5)                 → /reports/executive
//   - Maintenance (id 10)              → /reports/maintenance
//   - Wellness (ids 2, 3, 9, 11, 13)   → /reports/wellness    + subType string
//   - Environmental (ids 6, 7, 8, 12)  → /reports/environmental + subType int
const WELLNESS_TYPE_REVERSE = {
    2: 'rounds',
    3: 'check-in',
    9: 'missed-check-in',
    11: 'check-in-history',
    13: 'rounds-history',
};
const ENVIRONMENTAL_TYPE_REVERSE = {
    6: 1,
    7: 2,
    8: 3,
    12: 4,
};

export function resolveScheduleRoute(reportTypeId) {
    const id = parseInt(reportTypeId, 10);
    if (id === 1) return { path: '/reports/alerts/run', subType: null };
    if (id === 4) return { path: '/reports/hourly', subType: null };
    if (id === 5) return { path: '/reports/executive', subType: null };
    if (id === 10) return { path: '/reports/maintenance', subType: null };
    if (WELLNESS_TYPE_REVERSE[id]) return { path: '/reports/wellness', subType: WELLNESS_TYPE_REVERSE[id] };
    if (ENVIRONMENTAL_TYPE_REVERSE[id]) return { path: '/reports/environmental', subType: ENVIRONMENTAL_TYPE_REVERSE[id] };
    return { path: null, subType: null };
}

// ---------------------------------------------------------------------------
// Top-level: schedule row → router state for navigation
// ---------------------------------------------------------------------------
/**
 * Build the navigation payload for "Run Report Local".
 *
 * @param {object} row — a scheduled report row
 * @returns {{ path: string|null, state: object }}
 */
export function runStateFromSchedule(row) {
    if (!row) return { path: null, state: null };
    const { path, subType } = resolveScheduleRoute(row.report_type_id);
    if (!path) return { path: null, state: null };

    const filters = parseFilterTerms(row.report_filter_terms);
    const { startDate, endDate, rangeId } = intervalIdToRange(row.report_interval_id);

    return {
        path,
        state: {
            autoRun: true,
            filters,
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            rangeId,
            subType,
            sourceScheduleId: row.report_id ?? null,
            sourceDescription: row.description || '',
        },
    };
}
